import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import Select from "react-select";
import { FaEye, FaEyeSlash, FaPlus } from "react-icons/fa";
import { MdDelete, MdEdit } from "react-icons/md";
import { Box, Button, Typography, styled } from "@mui/material";
import { RxCross2 } from "react-icons/rx";
import heic2any from "heic2any";
import { useSelector } from "react-redux";
import FormPageHeader from "../admin/quizSystem/components/FormPageHeader";

const CircleContainer = styled("div")({
  width: 150,
  height: 150,
  borderRadius: "50%",
  border: "2px dashed #ccc",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  color: "#555",
  fontSize: "14px",
  cursor: "pointer",
  position: "relative",
  backgroundColor: "#f9f9f9",
  overflow: "hidden",
});

const ImagePreview = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "50%",
  position: "absolute",
  top: 0,
  left: 0,
});

const formPanelStyle = {
  background: "#fff",
  border: "1px solid #eef2f7",
  borderRadius: "10px",
  boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
  padding: "18px",
};

const selectMenuStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 1200 }),
  menu: (base) => ({ ...base, zIndex: 1200 }),
};

const getBusinessUnitId = (item) =>
  item?.id ?? item?.business_unit_id ?? item?.businessUnitId;

const getBusinessUnitName = (item) =>
  item?.business_unit ||
  item?.business_unit_name ||
  item?.businessUnitName ||
  item?.unit_name ||
  item?.name ||
  "";

const getLocationUnitId = (item) =>
  item?.id ?? item?.locations_unit_id ?? item?.location_id ?? item?.locationId;

const getLocationUnitName = (item) => item?.locations_units || "";

const buildBusinessUnitOptions = (items) =>
  items
    .map((item) => {
      const id = getBusinessUnitId(item);
      if (id === undefined || id === null || id === "") return null;

      return {
        value: String(id),
        label: [`B${id}`, getBusinessUnitName(item)]
          .filter(Boolean)
          .join(" - "),
        name: getBusinessUnitName(item),
      };
    })
    .filter(Boolean);

const buildLocationOptions = (items) =>
  items
    .map((item) => {
      const id = getLocationUnitId(item);
      if (id === undefined || id === null || id === "") return null;

      return {
        value: String(id),
        label: [`L-${id}`, getLocationUnitName(item)]
          .filter(Boolean)
          .join(" - "),
        name: getLocationUnitName(item),
      };
    })
    .filter(Boolean);

const normalizeAssignmentPairs = (pairs) =>
  pairs
    .map((pair) => ({
      business_unit: String(pair.business_unit),
      locations_units: String(pair.locations_units),
    }))
    .filter((pair) => pair.business_unit && pair.locations_units);

const toBusinessUnitLocationPayload = (pairs) =>
  pairs.map((pair) => ({
    business_unit: Number(pair.business_unit),
    locations_units: Number(pair.locations_units),
  }));

const getCreatedUserId = (result) => {
  if (typeof result?.data === "number" || typeof result?.data === "string") {
    return result.data;
  }

  return (
    result?.data?.user_id ||
    result?.data?.id ||
    result?.data?.people_id ||
    result?.data?.people?.id ||
    result?.data?.people_details?.id ||
    result?.data?.user?.id ||
    result?.data?.created_user?.id ||
    result?.user_id ||
    result?.id
  );
};

const canManageBusinessUnitAssignmentsForRole = (role) => {
  const normalizedRole = `${role || ""}`.toLowerCase().replace(/[\s_-]/g, "");

  return normalizedRole === "owner" || normalizedRole === "superadmin";
};

const isTruthyFlag = (value) => value === true;

const getNormalizedRole = (role) =>
  `${role || ""}`.toLowerCase().replace(/[\s_-]/g, "");

const filterPermissionTypes = (permissions, canViewSuperAdmin) =>
  permissions.filter((item) => {
    const normalizedType = getNormalizedRole(item.type);

    if (normalizedType === "owner") return false;
    if (normalizedType === "superadmin") return canViewSuperAdmin;

    return true;
  });

const formatDateInputValue = (value) => {
  if (!value) return "";

  return String(value).split(/[T\s]/)[0];
};

const AddAdministrationDetail = () => {
  const jwtToken = localStorage.getItem("jwt_token");
  const organisation_id = localStorage.getItem("organisation_id");
  const history = useHistory();
  const selectedOrganization = useSelector(
    (state) => state.organization.selectedOrganization
  );
  const canManageBusinessUnitAssignments =
    canManageBusinessUnitAssignmentsForRole(selectedOrganization?.user_role) ||
    canManageBusinessUnitAssignmentsForRole(
      localStorage.getItem("user_role")
    ) ||
    canManageBusinessUnitAssignmentsForRole(localStorage.getItem("type"));

  const [userDetail, setUserDetail] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    phone_number: "",
    employee_id: "",
    position: "",
    awarding_state_date: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [permissionTypes, setPermissionTypes] = useState([]);
  const [selectedPermissionType, setSelectedPermissionType] = useState(null);
  const [checkboxList, setCheckboxList] = useState([]);
  const [rewardCheckboxList, setRewardCheckboxList] = useState([]);
  const [businessUnitList, setBusinessUnitList] = useState([]);
  const [businessUnitLocations, setBusinessUnitLocations] = useState([]);
  const [selectedBusinessUnits, setSelectedBusinessUnits] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationUnitList, setLocationUnitList] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [imageKey, setImageKey] = useState(Date.now());
  const addCacheBuster = (url) => {
    if (!url) return url;
    if (url.startsWith("data:")) return url;
    if (url.startsWith("blob:")) return url;
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}t=${imageKey}`;
  };

  useEffect(() => {
    const fetchOrganisationSettings = async () => {
      if (!organisation_id) return false;

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/organisations/${organisation_id}`,
          {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await res.json();
        const organisation = data?.data?.organizations || data?.data || {};
        const canViewSuperAdmin = isTruthyFlag(organisation.view_super_admin);

        return canViewSuperAdmin;
      } catch (error) {
        return false;
      }
    };

    const fetchPermissionTypes = async () => {
      try {
        const canViewSuperAdmin = await fetchOrganisationSettings();
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/people-permission`,
          {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await res.json();

        if (res.ok && Array.isArray(data?.data)) {
          const perms = data.data.map((perm) => ({
            ...perm,
            value: perm.id,
            label: perm.type,
          }));

          const filteredPerms = filterPermissionTypes(perms, canViewSuperAdmin);

          setPermissionTypes(filteredPerms);

          const employeePerm = filteredPerms.find(
            (item) => item.type === "Employee"
          );

          if (employeePerm) {
            setSelectedPermissionType(employeePerm);
            onChangePermission(employeePerm);
          }
        } else {
          toast.error("Permission data is empty or malformed");
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
        toast.error("Permission dropdown API error");
      }
    };

    fetchPermissionTypes();
  }, [jwtToken, organisation_id]);

  useEffect(() => {
    const fetchBusinessUnits = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/all-business-units-names/${organisation_id}`,
          {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await res.json();
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];

        setBusinessUnitList(list);
      } catch (error) {
        toast.error("Business unit dropdown API error");
      }
    };

    const fetchLocationUnits = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/all-locations-units-names/${organisation_id}`,
          {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await res.json();
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];

        setLocationUnitList(list);
      } catch (error) {
        toast.error("Location dropdown API error");
      }
    };

    if (organisation_id) {
      fetchBusinessUnits();
      fetchLocationUnits();
    }
  }, [jwtToken, organisation_id]);

  useEffect(() => {
    if (!userDetail.first_name) return;

    const autoPassword = `mynameis${userDetail.first_name.toLowerCase()}`;

    setUserDetail((prev) => {
      if (!prev.password || prev.password.startsWith("mynameis")) {
        return {
          ...prev,
          password: autoPassword,
        };
      }
      return prev;
    });
  }, [userDetail.first_name]);

  useEffect(() => {
    if (!userDetail.email || !userDetail.email.includes("@")) return;

    const emailPrefix = userDetail.email.split("@")[0];

    setUserDetail((prev) => {
      if (!prev.employee_id || prev.employee_id === prev.email?.split("@")[0]) {
        return {
          ...prev,
          employee_id: emailPrefix,
        };
      }
      return prev;
    });
  }, [userDetail.email]);

  const onChangeHandler = (e) => {
    setUserDetail({ ...userDetail, [e.target.id]: e.target.value });
  };

  const handleImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const onChangePermission = (permission) => {
    setSelectedPermissionType(permission);

    const updatedPermissions = permission.permissions_list.map(
      (perm, index) => ({
        name: perm,
        checked: !(
          (permission.type === "External Instructor" &&
            (index === 0 ||
              index === permission.permissions_list.length - 1)) ||
          (permission.type === "Employee" &&
            index === permission.permissions_list.length - 1)
        ),
      })
    );
    setCheckboxList(updatedPermissions);

    const rewardPermissions =
      permission.reward_permissions_list?.map((perm) => ({
        name: perm,
        checked: true,
      })) || [];
    setRewardCheckboxList(rewardPermissions);
  };

  const onCheckboxHandler = (e, index) => {
    const updated = [...checkboxList];
    updated[index].checked = e.target.checked;
    setCheckboxList(updated);
  };

  const onRewardCheckboxHandler = (e, index) => {
    const updated = [...rewardCheckboxList];
    updated[index].checked = e.target.checked;
    setRewardCheckboxList(updated);
  };



  const handleImageChange = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const fileType = file.type;

    try {
      let finalFile = file;

      // Convert HEIC/HEIF to JPEG
      if (fileType === "image/heic" || fileType === "image/heif") {
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.8,
        });

        finalFile = new File(
          [convertedBlob],
          file.name.replace(/\.[^/.]+$/, ".jpeg"),
          {
            type: "image/jpeg",
          }
        );
      }

      setSelectedImage(finalFile);
      setImagePreviewUrl(URL.createObjectURL(finalFile));
      setImageKey(Date.now());
    } catch (error) {
      console.error("Image handling failed:", error);
      toast.error("Unsupported image format or conversion failed.");
    }
  };

  const handleRemoveImage = (e) => {
    e.preventDefault();
    setSelectedImage(null);
    setImagePreviewUrl("");
    setImageKey(Date.now());
  };

  const onSubmitHandler = async (e) => {
    e.preventDefault();

    if (!userDetail.password) return toast.error("Password is required.");
    if (!selectedPermissionType)
      return toast.error("Please select an account type.");

    let base64Image = null;
    if (selectedImage) {
      try {
        base64Image = await handleImageToBase64(selectedImage);
      } catch (err) {
        return toast.error("Image conversion failed.");
      }
    }

    const { awarding_state_date, ...submitUserDetail } = userDetail;

    const payload = {
      ...submitUserDetail,
      permission: {
        type: selectedPermissionType.type,
        checkbox: checkboxList
          .map((box, i) => (box.checked ? i : null))
          .filter((v) => v !== null)
          .join(","),
        reward_checkbox: rewardCheckboxList
          .map((box, i) => (box.checked ? i : null))
          .filter((v) => v !== null)
          .join(","),
        awarding_state_date:
          formatDateInputValue(awarding_state_date) || null,
      },
      organisation_id,
      profile_image: base64Image,
    };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/add-user-to-organisation`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      const result = await res.json();

      if (res.ok) {
        const userId = getCreatedUserId(result);

        if (userId && canManageBusinessUnitAssignments) {
          const businessUnitRes = await fetch(
            `${process.env.REACT_APP_API_URL}/employee-business-units`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${jwtToken}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                organisation_id: Number(organisation_id),
                user_id: Number(userId),
                business_units: selectedBusinessUnits.map(Number),
                locations_units: selectedLocations.map(Number),
              }),
            }
          );
          const businessUnitResult = await businessUnitRes.json();

          if (!businessUnitRes.ok) {
            throw new Error(
              businessUnitResult.error || "Failed to save business units"
            );
          }
        } else if (
          canManageBusinessUnitAssignments &&
          (selectedBusinessUnits.length > 0 || selectedLocations.length > 0)
        ) {
          throw new Error(
            "User created, but user id was missing for business unit and location assignment"
          );
        }

        if (result.data?.profile_image) {
          setImagePreviewUrl(result.data.profile_image);
          setImageKey(Date.now());
        }

        toast.success(result.message || "User created successfully");
        setTimeout(() => {
          history.push("/administration");
        }, 500);
      } else {
        toast.error(result.error || "Something went wrong");
      }
    } catch (error) {
      toast.error(error.message || "Submission failed.");
    }
  };

  return (
    <div className="min-h-[93vh] overflow-auto bg-white p-3 text-[#20232A] sm:p-4">
      <FormPageHeader
        title="Add Administration Details"
        onBack={() => history.push("/administration")}
      />

      <form onSubmit={onSubmitHandler} style={formPanelStyle}>
        <h6>User Details</h6>
        <Box
          mt={2}
          mb={2}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Profile Photo:
          </Typography>
          <input
            accept="image/png, image/jpeg, image/jpg, image/webp, image/heic, image/heif"
            type="file"
            id="upload-image"
            style={{ display: "none" }}
            onChange={handleImageChange}
          />
          <label
            htmlFor="upload-image"
            style={{ position: "relative", display: "inline-block" }}
          >
            <CircleContainer>
              {imagePreviewUrl ? (
                <ImagePreview
                  key={imageKey}
                  src={addCacheBuster(imagePreviewUrl)}
                  alt="Profile Preview"
                />
              ) : (
                <span>Upload your profile photo.</span>
              )}
            </CircleContainer>

            {imagePreviewUrl && (
              <Button
                onClick={handleRemoveImage}
                sx={{
                  minWidth: "unset",
                  padding: 0,
                  position: "absolute",
                  top: "10px",
                  right: "-10px",
                  backgroundColor: "#f44336",
                  color: "#fff",
                  borderRadius: "50%",
                  width: 28,
                  height: 28,
                  "&:hover": {
                    backgroundColor: "#d32f2f",
                  },
                }}
              >
                <RxCross2 size={16} />
              </Button>
            )}
          </label>
        </Box>

        <div className="row">
          {[
            "first_name",
            "last_name",
            "email",
            "password",
            "phone_number",
            "employee_id",
            "position",
          ].map((field) => (
            <div className="col-md-6 mt-2" key={field}>
              <label htmlFor={field}>
                {field
                  .replace("_", " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase())}
                {field !== "employee_id" &&
                  field !== "phone_number" &&
                  field !== "position" && <span className="danger">*</span>}
              </label>
              <div className="input-group">
                <input
                  type={
                    field === "email"
                      ? "email"
                      : field === "phone_number"
                      ? "number"
                      : field === "password"
                      ? showPassword
                        ? "text"
                        : "password"
                      : "text"
                  }
                  className="form-control"
                  id={field}
                  required={
                    field !== "employee_id" &&
                    field !== "phone_number" &&
                    field !== "position"
                  }
                  value={userDetail[field]}
                  onChange={onChangeHandler}
                />
                {field === "password" && (
                  <span
                    className="input-group-text"
                    style={{ cursor: "pointer" }}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <h6 className="mt-3">User Permissions</h6>
        <div className="row align-items-end">
          <div className="col-md-6 mt-2">
            <label>Account Type</label>
            <Select
              options={permissionTypes}
              value={selectedPermissionType}
              onChange={onChangePermission}
              placeholder="Select Account Type"
            />
          </div>
          <div className="col-md-6 mt-2">
            <label htmlFor="awarding_state_date">Awarding State Date</label>
            <input
              type="date"
              className="form-control"
              id="awarding_state_date"
              value={formatDateInputValue(userDetail.awarding_state_date)}
              onChange={onChangeHandler}
            />
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-md-6 ">
            <label>People Risk Analysis Permissions</label>
            {checkboxList.length > 0 ? (
              checkboxList.map((perm, index) => (
                <div className="mb-2 flex items-start gap-2" key={index}>
                  <input
                    className="mt-1 h-4 w-4 flex-shrink-0 accent-[#1a73e8]"
                    type="checkbox"
                    id={`perm_${index}`}
                    checked={perm.checked}
                    onChange={(e) => onCheckboxHandler(e, index)}
                  />
                  <label
                    className="m-0 leading-5"
                    htmlFor={`perm_${index}`}
                    style={{ fontWeight: 400 }}
                  >
                    {perm.name
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                  </label>
                </div>
              ))
            ) : (
              <p className="text-muted">
                Select account type to view permissions
              </p>
            )}
          </div>

          {canManageBusinessUnitAssignments && (
            <div className="col-md-6 mt-3 border-left pl-4 md:mt-0">
              <label className="mb-2">Business Unit & Location</label>
              <div className="row">
                <div className="col-6">
                  <h6 className="text-sm font-semibold mb-2">Business Units</h6>
                  <div className="overflow-auto border rounded p-2" style={{ maxHeight: '200px' }}>
                    {businessUnitList.length > 0 ? (
                      businessUnitList.map((bu) => {
                        const id = String(getBusinessUnitId(bu));
                        const name = getBusinessUnitName(bu);
                        return (
                          <div className="mb-2 flex items-start gap-2" key={id}>
                            <input
                              className="mt-1 h-4 w-4 flex-shrink-0 accent-[#1a73e8]"
                              type="checkbox"
                              id={`bu_${id}`}
                              checked={selectedBusinessUnits.includes(id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedBusinessUnits((prev) => [...prev, id]);
                                } else {
                                  setSelectedBusinessUnits((prev) => prev.filter(x => x !== id));
                                }
                              }}
                            />
                            <label className="m-0 leading-5" htmlFor={`bu_${id}`} style={{ fontWeight: 400 }}>
                              B{id} - {name}
                            </label>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-muted text-sm">No business units available</p>
                    )}
                  </div>
                </div>

                <div className="col-6">
                  <h6 className="text-sm font-semibold mb-2">Locations</h6>
                  <div className="overflow-auto border rounded p-2" style={{ maxHeight: '200px' }}>
                    {locationUnitList.length > 0 ? (
                      locationUnitList.map((loc) => {
                        const id = String(getLocationUnitId(loc));
                        const name = getLocationUnitName(loc);
                        return (
                          <div className="mb-2 flex items-start gap-2" key={id}>
                            <input
                              className="mt-1 h-4 w-4 flex-shrink-0 accent-[#1a73e8]"
                              type="checkbox"
                              id={`loc_${id}`}
                              checked={selectedLocations.includes(id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedLocations((prev) => [...prev, id]);
                                } else {
                                  setSelectedLocations((prev) => prev.filter(x => x !== id));
                                }
                              }}
                            />
                            <label className="m-0 leading-5" htmlFor={`loc_${id}`} style={{ fontWeight: 400 }}>
                              L{id} - {name}
                            </label>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-muted text-sm">No locations available</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <button type="submit" className="btn mt-4 mb-3 bg_color">
          Add User
        </button>
      </form>


    </div>
  );
};

export default AddAdministrationDetail;
